import { useState, useMemo } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import api from '@/lib/api'
import { Loader2, Play } from 'lucide-react'

type Fund = { id: string; name: string; ticker: string; source: string }

type Tail = { var_95: number | null; es_95: number | null; var_99: number | null; es_99: number | null }
type EnsembleResp = {
  run_id: number; n_obs: number
  weights: Record<string, number>
  raw_weights: Record<string, number>
  tail: Tail
  disagreement: Record<string, number | null>
  via_bridge?: boolean; bridge_runtime_ms?: number
}

export default function RiskEnsemble() {
  const [search, setSearch] = useState('')
  const [fundId, setFundId] = useState('')
  const [universe, setUniverse] = useState('narrow')
  const [window, setWindow] = useState('full')
  const [allowGpu, setAllowGpu] = useState(false)

  const fundsQ = useQuery<Fund[]>({
    queryKey: ['fa-funds'],
    queryFn: () => api.get('/fund-analysis/funds').then(r => r.data),
  })

  const run = useMutation<EnsembleResp, unknown>({
    mutationFn: () => api.post('/risk-stats/ensemble', {
      fund_id: fundId, universe, time_window: window, allow_gpu: allowGpu,
    }).then(r => r.data),
  })

  const filteredFunds = useMemo(() => {
    if (!fundsQ.data) return []
    const s = search.toLowerCase()
    return fundsQ.data.filter(f => !s || f.name?.toLowerCase().includes(s) || f.ticker?.toLowerCase().includes(s)).slice(0, 300)
  }, [fundsQ.data, search])

  const pct = (v: number | null | undefined, d = 2) => v == null ? '—' : `${(v * 100).toFixed(d)}%`
  const num = (v: number | null | undefined, d = 3) => v == null ? '—' : v.toFixed(d)

  const engines = Object.keys(run.data?.weights ?? {}).sort()

  // Bar widths for the weight chart
  const maxW = Math.max(...Object.values(run.data?.weights ?? { x: 0 }), 0.001)

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Ensemble VaR</h1>
        <p className="text-muted-foreground">
          Structural ensemble across <span className="font-mono">PCA Vine · Ortho Vine · PolyModel Narrow · PolyModel Wide · Signature</span>.
          Adaptive weights via pinball loss and Fisher-Z correlation regime. Signature requires the GPU bridge.
        </p>
      </div>

      {/* Controls */}
      <div className="bg-card border border-border rounded-lg p-4 space-y-4">
        <div className="flex flex-col md:flex-row gap-3">
          <input type="text" placeholder="Search by name or ticker..." value={search} onChange={e => setSearch(e.target.value)} className="input flex-1" />
          <select value={fundId} onChange={e => setFundId(e.target.value)} className="input w-full md:w-[28rem]">
            <option value="">-- Select fund --</option>
            {filteredFunds.map(f => (
              <option key={f.id} value={f.id}>{f.name} ({f.ticker}) [{f.source}]</option>
            ))}
          </select>
        </div>
        <div className="flex flex-wrap gap-3 items-center">
          <label className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Universe</span>
            <select value={universe} onChange={e => setUniverse(e.target.value)} className="input">
              <option value="narrow">narrow</option>
              <option value="wide">wide</option>
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Window</span>
            <select value={window} onChange={e => setWindow(e.target.value)} className="input">
              <option value="full">full</option>
              <option value="10y">10y</option>
              <option value="5y">5y</option>
              <option value="2y">2y</option>
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm" title={'Deploy ft.risk.bridge on a GPU host to enable (see repo deploy/ folder).'}>
            <input type="checkbox" checked={allowGpu} onChange={e => setAllowGpu(e.target.checked)} />
            <span>Allow GPU (include Signature via bridge)</span>
          </label>
          <button
            type="button"
            disabled={!fundId || run.isPending}
            onClick={() => run.mutate()}
            className="ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground disabled:opacity-40"
          >
            {run.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
            Run ensemble
          </button>
        </div>
        {run.isError && (
          <div className="text-sm text-destructive">error — {String((run.error as any)?.response?.data?.detail ?? run.error)}</div>
        )}
      </div>

      {/* Results */}
      {run.data && (
        <>
          {/* Tail metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Metric label="VaR 95"  value={pct(run.data.tail.var_95)} />
            <Metric label="ES 95"   value={pct(run.data.tail.es_95)} />
            <Metric label="VaR 99"  value={pct(run.data.tail.var_99)} />
            <Metric label="ES 99"   value={pct(run.data.tail.es_99)} />
          </div>

          {/* Weights */}
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold">Adaptive engine weights</h2>
              <span className="text-xs text-muted-foreground">
                run_id <span className="font-mono">{run.data.run_id}</span> · n_obs {run.data.n_obs}
                {run.data.via_bridge && <> · via bridge ({run.data.bridge_runtime_ms}ms)</>}
              </span>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2 px-3">Engine</th>
                  <th className="text-right py-2 px-3">Raw</th>
                  <th className="text-right py-2 px-3">Mix</th>
                  <th className="text-left py-2 px-3 w-1/2">Contribution</th>
                  <th className="text-right py-2 px-3">Disagreement</th>
                </tr>
              </thead>
              <tbody>
                {engines.map(name => {
                  const mix = run.data!.weights[name] ?? 0
                  const raw = run.data!.raw_weights[name] ?? 0
                  const dis = run.data!.disagreement?.[name] ?? null
                  return (
                    <tr key={name} className="border-b border-border/50">
                      <td className="py-2 px-3 font-mono text-xs">{name}</td>
                      <td className="py-2 px-3 text-right font-mono text-xs">{num(raw)}</td>
                      <td className="py-2 px-3 text-right font-mono text-xs font-semibold">{num(mix)}</td>
                      <td className="py-2 px-3">
                        <div className="h-2 bg-muted rounded overflow-hidden">
                          <div
                            className="h-2 bg-primary"
                            style={{ width: `${Math.round((mix / maxW) * 100)}%` }}
                          />
                        </div>
                      </td>
                      <td className="py-2 px-3 text-right font-mono text-xs">{num(dis)}</td>
                    </tr>
                  )
                })}
                {engines.length === 0 && (
                  <tr><td colSpan={5} className="py-4 text-center text-muted-foreground">No weights returned.</td></tr>
                )}
              </tbody>
            </table>
            <div className="text-xs text-muted-foreground mt-3">
              Raw weights come from pinball loss on recent backtest history; mix weights apply the Fisher-Z regime blending.
              Disagreement = pairwise dispersion of the engine's tail estimate vs the ensemble median.
            </div>
          </div>
        </>
      )}
    </div>
  )
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-card border border-border rounded-lg p-4">
      <div className="text-xs text-muted-foreground uppercase tracking-wider">{label}</div>
      <div className="text-xl font-semibold mt-1 font-mono">{value}</div>
    </div>
  )
}
